import { IconButton as DesignIconButton, Tooltip as DesignTooltip } from "@mui/material";
import { CloseOutlined as DesignClose, } from "@mui/icons-material";
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import type { FlowMode } from "../../types/botFlow";
import { FLOW_MODE_LABELS } from "../../types/botFlow";

interface BotModeChangeConfirmDialogProps {
  fromMode: FlowMode;
  open: boolean;
  saving: boolean;
  toMode: FlowMode;
  onCancel: () => void;
  onConfirm: () => void;
}

const PRECONDITIONS = [
  "Cost circuit-breaker is live for bot sends.",
  "Bot draft and approval activity is visible in the client feed.",
  "Safety keyword smoke tests are passing.",
  "Fallback draft approval flow is ready for anything escalated.",
];

const BotModeChangeConfirmDialog: React.FC<BotModeChangeConfirmDialogProps> = ({
  fromMode,
  open,
  saving,
  toMode,
  onCancel,
  onConfirm,
}) => {
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [understood, setUnderstood] = useState(false);
  const isL4 = toMode === "L4_full_auto";

  useEffect(() => {
    if (open) {
      setChecked({});
      setUnderstood(false);
    }
  }, [open, fromMode, toMode]);

  const allPreconditionsChecked = useMemo(
    () => PRECONDITIONS.every((item) => checked[item]),
    [checked]
  );

  const canConfirm = !isL4 && allPreconditionsChecked && understood && !saving;

  return (
    <Dialog open={open} onClose={saving ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 7, position: "relative" }}><span style={{ position: "absolute", right: 8, top: 8 }}><DesignTooltip title="Close"><span><DesignIconButton aria-label="Close" onClick={onCancel} disabled={saving}  sx={{ width: 40, height: 40 }}><DesignClose sx={{ fontSize: 20 }} /></DesignIconButton></span></DesignTooltip></span>
        {isL4 ? "L4 is locked" : "Confirm L3 auto-FAQ mode"}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity={isL4 ? "error" : "warning"}>
            {isL4
              ? "L4 full-auto mode is code-locked and cannot be enabled from Settings."
              : "L3 can send FAQ-classified replies without operator approval. Safety-classified drafts still escalate."}
          </Alert>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Requested change
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {FLOW_MODE_LABELS[fromMode]} to {FLOW_MODE_LABELS[toMode]}
            </Typography>
          </Box>

          {isL4 ? (
            <Typography variant="body2" color="text.secondary">
              Use the engineering unlock checklist before this mode is made
              available. Cancel keeps the current mode unchanged.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {PRECONDITIONS.map((item) => (
                <FormControlLabel
                  key={item}
                  control={
                    <Checkbox
                      checked={Boolean(checked[item])}
                      onChange={(event) =>
                        setChecked((current) => ({
                          ...current,
                          [item]: event.target.checked,
                        }))
                      }
                    />
                  }
                  label={item}
                />
              ))}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={understood}
                    onChange={(event) => setUnderstood(event.target.checked)}
                  />
                }
                label="I understand this mode can send auto-FAQ replies without approval."
              />
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={isL4 ? "inherit" : "warning"}
          onClick={onConfirm}
          disabled={!canConfirm}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          {isL4 ? "Locked" : "Confirm L3"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BotModeChangeConfirmDialog;
